import { useCallback, useEffect, useRef, useState } from "react";
import { createAuthenticatedPublicClient } from "@connect/data/public/client";
import type { components } from "@connect/data/public/schema";
import { classifyKycError } from "./classify-kyc-error";
import type { ClassifiedKycError } from "./classify-kyc-error.types";
import { customerApiHeaders } from "./customer-api-headers";
import { runWithTransientRetry } from "./retry-backoff";

type Requirements = components["schemas"]["Requirements"];
type CustomerKyc = components["schemas"]["CustomerKyc"];
type CustomerKycStatus = components["schemas"]["CustomerKycStatus"];
type IdentityVerificationChallenge = components["schemas"]["IdentityVerificationChallenge"];

export interface FetchedKyc {
  requirements: Requirements;
  status: CustomerKycStatus;
  challenge: IdentityVerificationChallenge | undefined;
}

export type KycFetchResult =
  | { ok: true; kyc: FetchedKyc }
  | { ok: false; error: ClassifiedKycError };

function toFetchedKyc(
  next: Pick<CustomerKyc, "requirements" | "status" | "challenge">,
): FetchedKyc {
  return {
    requirements: next.requirements,
    status: next.status,
    challenge: next.challenge,
  };
}

export interface UseKycRequirementsParams {
  customerId: string | null | undefined;
  apiBaseUrl: string;
  clientHeaders: Record<string, string>;
  enabled: boolean;
  retryWait?: (delayMs: number) => Promise<void>;
}

export interface UseKycRequirementsResult {
  requirements: Requirements | undefined;
  status: CustomerKycStatus | undefined;
  challenge: IdentityVerificationChallenge | undefined;
  loading: boolean;
  error: ClassifiedKycError | null;
  fetchKyc: (signal?: AbortSignal) => Promise<KycFetchResult>;
  refetch: () => Promise<FetchedKyc | undefined>;
  reportKycError: (error: ClassifiedKycError) => void;
  clearError: () => void;
  syncKyc: (kyc: Pick<CustomerKyc, "requirements" | "status" | "challenge">) => FetchedKyc;
}

async function prepareCustomerKycRequest(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  signal?: AbortSignal,
) {
  signal?.throwIfAborted();
  const client = createAuthenticatedPublicClient({
    baseUrl: apiBaseUrl,
    headers: await customerApiHeaders(clientHeaders),
  });
  signal?.throwIfAborted();
  return () =>
    client.GET("/platform/v1/customers/{id}", {
      params: { path: { id: customerId } },
      signal,
    });
}

async function fetchCustomerKycAttempt(
  customerId: string,
  apiBaseUrl: string,
  clientHeaders: Record<string, string>,
  signal?: AbortSignal,
) {
  let request: Awaited<ReturnType<typeof prepareCustomerKycRequest>>;
  try {
    request = await prepareCustomerKycRequest(customerId, apiBaseUrl, clientHeaders, signal);
  } catch (caught) {
    return {
      ok: false as const,
      error: classifyKycError({ error: caught, source: "local" }),
    };
  }
  let result: Awaited<ReturnType<typeof request>>;
  try {
    result = await request();
  } catch (caught) {
    return {
      ok: false as const,
      error: classifyKycError({ error: caught, source: "transport" }),
    };
  }
  const { data, error: apiError, response } = result;
  const kyc = data?.data?.kyc;
  if (kyc) {
    return { ok: true as const, kyc };
  }
  const httpStatus = response?.status;
  const isHttpFailure = httpStatus !== undefined && httpStatus >= 400;
  return {
    ok: false as const,
    error: classifyKycError({
      error: apiError ?? (isHttpFailure ? undefined : new Error("kyc_fetch_failed")),
      httpStatus,
      source: apiError === undefined && !isHttpFailure ? "local" : "api",
    }),
  };
}

export function useKycRequirements({
  customerId,
  apiBaseUrl,
  clientHeaders,
  enabled,
  retryWait,
}: UseKycRequirementsParams): UseKycRequirementsResult {
  const [kyc, setKyc] = useState<FetchedKyc | undefined>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<ClassifiedKycError | null>(null);
  const hasRequirementsRef = useRef(false);

  useEffect(() => {
    hasRequirementsRef.current = kyc !== undefined;
  }, [kyc]);

  const syncKyc = useCallback(
    (next: Pick<CustomerKyc, "requirements" | "status" | "challenge">): FetchedKyc => {
      const snapshot = toFetchedKyc(next);
      hasRequirementsRef.current = true;
      setKyc(snapshot);
      setError(null);
      return snapshot;
    },
    [],
  );

  const failFetch = useCallback((classified: ClassifiedKycError) => {
    if (hasRequirementsRef.current && classified.kind !== "terminal") {
      return;
    }
    if (!hasRequirementsRef.current) {
      setKyc(undefined);
    }
    setError(classified);
  }, []);

  const fetchKyc = useCallback(
    async (signal?: AbortSignal): Promise<KycFetchResult> => {
      if (!customerId) {
        return {
          ok: false,
          error: classifyKycError({ error: new Error("missing_customer_id"), source: "local" }),
        };
      }
      try {
        const result = await runWithTransientRetry(
          () => fetchCustomerKycAttempt(customerId, apiBaseUrl, clientHeaders, signal),
          (attempt) => !attempt.ok && attempt.error.kind === "transient",
          retryWait,
          signal,
        );
        return result.ok ? { ok: true, kyc: toFetchedKyc(result.kyc) } : result;
      } catch (caught) {
        return {
          ok: false,
          error: classifyKycError({ error: caught, source: "local" }),
        };
      }
    },
    [apiBaseUrl, clientHeaders, customerId, retryWait],
  );

  const reportKycError = useCallback((classified: ClassifiedKycError) => {
    setError(classified);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const refetch = useCallback(async (): Promise<FetchedKyc | undefined> => {
    if (!customerId) {
      return undefined;
    }
    if (!hasRequirementsRef.current) {
      setLoading(true);
    }
    setError(null);
    try {
      const result = await fetchKyc();
      if (!result.ok) {
        failFetch(result.error);
        return undefined;
      }
      return syncKyc(result.kyc);
    } finally {
      setLoading(false);
    }
  }, [customerId, failFetch, fetchKyc, syncKyc]);

  useEffect(() => {
    if (!enabled || !customerId || hasRequirementsRef.current) {
      return;
    }
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect -- loading flag for the initial KYC fetch
    void refetch();
  }, [customerId, enabled, refetch]);

  return {
    requirements: kyc?.requirements,
    status: kyc?.status,
    challenge: kyc?.challenge,
    loading,
    error,
    fetchKyc,
    refetch,
    reportKycError,
    clearError,
    syncKyc,
  };
}
